/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { SortDirection } from '@angular/material/sort';
import { JsonReply, SelectOptionGroup } from '@grupo-constant/next-dtype/types';
import { cloneDeep } from 'lodash-es';
import { BehaviorSubject, catchError, map, Observable, tap, throwError } from 'rxjs';
import { ServicesFormSummary, Pagination, SearchFormsBody, RetrieveServiceFormOptionsBody, ServiceFormsSearchOptionsFilter, ScheduleInfo, SearchFormsReply, DownloadFormsBody, RetrieveServiceFormOptionsReply, DeleteFormRequest } from './service-forms.types';
import { DialogsService } from '../../../dialogs/dialogs.service';
import { HttpService } from '../../../core/http/http.service';
import { LocalizePipe } from '../../../core/pipes/localize.pipe';
import { NextMethodsEnum } from '../../../core/enums/next-methods.enum';

@Injectable({
    providedIn: 'root'
})
export class ServiceFormsService {

    private _loading: BehaviorSubject<boolean> = new BehaviorSubject(true);
    private _loadingList: BehaviorSubject<boolean> = new BehaviorSubject(true);
    private _serviceFormsList: BehaviorSubject<ServicesFormSummary[] | null> = new BehaviorSubject(null);
    private _pagination: BehaviorSubject<Pagination> = new BehaviorSubject(undefined);
    private _viewServiceFormsList: BehaviorSubject<ServicesFormSummary[]> = new BehaviorSubject(undefined);
    private _bodyWithFilters: BehaviorSubject<SearchFormsBody> = new BehaviorSubject(undefined);
    private _availableOptions: BehaviorSubject<ServiceFormsSearchOptionsFilter[]> = new BehaviorSubject(undefined);
    private _dateFrom: BehaviorSubject<Date> = new BehaviorSubject(undefined);
    private _dateTo: BehaviorSubject<Date> = new BehaviorSubject(undefined);
    private _schedule: BehaviorSubject<ScheduleInfo> = new BehaviorSubject(undefined);
    private _taskTypes: BehaviorSubject<SelectOptionGroup[]> = new BehaviorSubject(undefined);
    private _planningAreas: BehaviorSubject<any[]> = new BehaviorSubject(undefined);
    private _formOptions: BehaviorSubject<SelectOptionGroup[]> = new BehaviorSubject(undefined);

    constructor(
        private dialogsService: DialogsService,
        private httpService: HttpService,
        private localize: LocalizePipe,
    ) { }

    get serviceFormsList$(): Observable<ServicesFormSummary[]> {
        return this._serviceFormsList.asObservable();
    }

    get loading$(): Observable<boolean> {
        return this._loading.asObservable();
    }

    get loadingList$(): Observable<boolean> {
        return this._loadingList.asObservable();
    }

    get pagination$(): Observable<any> {
        return this._pagination.asObservable();
    }

    get bodyWithFilters$(): Observable<SearchFormsBody> {
        return this._bodyWithFilters.asObservable();
    }

    get viewServiceFormsList$(): Observable<ServicesFormSummary[]> {
        return this._viewServiceFormsList.asObservable();
    }

    get availableOptions$(): Observable<ServiceFormsSearchOptionsFilter[]> {
        return this._availableOptions.asObservable();
    }

    get taskTypes$(): Observable<SelectOptionGroup[]> {
        return this._taskTypes.asObservable();
    }

    get planningAreas$(): Observable<any[]> {
        return this._planningAreas.asObservable();
    }

    get formOptions$(): Observable<SelectOptionGroup[]> {
        return this._formOptions.asObservable();
    }

    get schedule$(): Observable<ScheduleInfo> {
        return this._schedule.asObservable();
    }

    get dateFrom$(): Observable<Date> {
        return this._dateFrom.asObservable();
    }

    get dateTo$(): Observable<Date> {
        return this._dateTo.asObservable();
    }

    setDateFrom(date: Date) {
        this._dateFrom.next(date);
    }

    setDateTo(date: Date) {
        this._dateTo.next(date);
    }

    getViewForms(body: SearchFormsBody, refresh: boolean = false, page: number = 0, size: number = 10,
        field: string = 'receivedDate', dir: SortDirection = 'desc'): Observable<ServicesFormSummary[]> {
        this.setLoadingList(true);
        return this.searchForms(body, refresh).pipe(
            map((forms) => {
                // Clone the forms to prevent accidental updates
                let formsList = cloneDeep(this.sortForms(forms, field, dir));
                const formsLength = formsList.length;

                // Paginate - Start

                // Calculate pagination details
                const begin = page * size;
                const end = Math.min((size * (page + 1)), formsLength);
                const lastPage = Math.max(Math.ceil(formsLength / size), 1);

                // Prepare the pagination object
                let pagination: Partial<Pagination>;

                // If the requested page number is bigger than
                // the last possible page number, return null for
                // tasks but also send the last possible page so
                // the app can navigate to there
                if (page > lastPage) {
                    pagination = {
                        lastPage
                    };
                } else {
                    // Paginate the results by 10
                    formsList = formsList.slice(begin, end);

                    // Prepare the pagination mock-api
                    pagination = {
                        length: formsLength,
                        size,
                        page,
                        lastPage: lastPage,
                        startIndex: begin,
                        endIndex: end - 1
                    };
                }

                this._viewServiceFormsList.next(formsList);
                this._pagination.next(pagination as Pagination);
                this.setLoadingList(false);
                return formsList;
            })
        );
    }

    setLoading(state: boolean) {
        this._loading.next(state);
    }

    setLoadingList(state: boolean) {
        this._loadingList.next(state);
    }

    setBodyWithFilters(body: SearchFormsBody) {
        this._bodyWithFilters.next(body);
    }

    getServiceFormsSearchOptions(body: RetrieveServiceFormOptionsBody) {
        return this.retrieveServiceFormsOptions(body).pipe(
            catchError((error) => {
                this.dialogsService.openErrorDialog({
                message: error?.echo ? this.localize.transform(error.echo) : undefined
                });
                return throwError(() => error);
            }),
            tap((reply) => {
                this._availableOptions.next(reply.availableSearchOptions);
                this._taskTypes.next(reply.taskType);
                this._planningAreas.next(reply.planningArea);
                this._schedule.next(reply.schedule);
                this._formOptions.next(reply.form);
                return reply;
            })
        );
    }

    downloadServiceForms(body: DownloadFormsBody) {
        return this.httpService.postWithHeaders<any>(NextMethodsEnum.downloadServiceFormsXSSF, body, 'blob');
    }

    deleteForm(request: DeleteFormRequest) {
        return this.httpService.post<JsonReply>(NextMethodsEnum.deleteForm, request);
    }

    private searchForms(body?: SearchFormsBody, refresh: boolean = false): Observable<ServicesFormSummary[]> {
        const formsList = this._serviceFormsList.getValue();
        if (refresh || !formsList) {
            return this.retrieveServiceForms(body).pipe(
                map((reply) => {
                    const newForms = reply.taskFormSummary ?? [];
                    return newForms;
                }),
                tap(newForms => this._serviceFormsList.next(newForms))
            );
        } else {
            return this.serviceFormsList$;
        }
    }

    private retrieveServiceFormsOptions(body: RetrieveServiceFormOptionsBody) {
        return this.httpService.post<RetrieveServiceFormOptionsReply>(NextMethodsEnum.retrieveServiceFormsOptions, body);
    }

    private retrieveServiceForms(body: SearchFormsBody): Observable<SearchFormsReply> {
        return this.httpService.post(NextMethodsEnum.searchServiceForms, body);
    }

    private sortForms(forms: ServicesFormSummary[], field: string, dir: SortDirection) {
        return forms.sort((a, b) => {
            switch (field) {
                case 'formName':
                    return this.compareStrings(this.localize.transform(a.caption) ?? '', this.localize.transform(b.caption) ?? '', dir);
                case 'taskName':
                    return this.compareStrings(a.taskId ?? '', b.taskId ?? '', dir);
                case 'taskType':
                    return this.compareStrings(a.taskTypeName ?? '', b.taskTypeName ?? '', dir);
                case 'userName':
                    return this.compareStrings(a.userName ?? '', b.userName ?? '', dir);
                case 'taskDate':
                    return this.compareStrings(a.taskDate ?? '', b.taskDate ?? '', dir);
                case 'createdDate':
                    return this.compareStrings(a.creationDate ?? '', b.creationDate ?? '', dir);
                case 'queueDate':
                    return this.compareStrings(a.queueDate ?? '', b.queueDate ?? '', dir);
                case 'receivedDate':
                    return this.compareStrings(a.receivedDate ?? '', b.receivedDate ?? '', dir);
            }
            return 0;
        });
    }

    private compareStrings(string1: string, string2: string, dir: SortDirection) {
        if (string1 === '') {
            if (string2 === '') {
                return 0;
            } else {
                return dir === 'asc' ? 0 : -1;
            }
        } else if (string2 === '') {
            if (string1 === '') {
                return 0;
            } else {
                return dir === 'asc' ? -1 : 0;
            }
        } else {
            return dir === 'asc' ? string1.localeCompare(string2) : string2.localeCompare(string1);
        }
    }

}
